import React, { useState, useCallback, useEffect } from 'react';
import FinancialAnalysisUploadSection from './FinancialAnalysisUploadSection';
import FinancialAnalysisResultsSection from './FinancialAnalysisResultsSection';
import FinancialInsightsCard from './FinancialInsightsCard';
import { getFullApiUrl } from '../config';
import { addAuthHeader } from '../utils/auth';

const FinancialAnalysisContent = ({ onProcessingStateChange }) => {
  const [extractedData, setExtractedData] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);  // ID analysis yang sedang aktif (untuk cache insights)
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  const handleExtractionComplete = (pivotData, years) => {
    setExtractedData({ pivotData, years });
    setIsDataLoaded(true);
    setAiInsights(null);
    setAnalysisId(null);  // Reset analysis ID saat upload baru
  };

  const handleAnalysisCalculated = useCallback((calculatedData) => {
    setAnalysisResults(calculatedData);
  }, []);

  const handleInsightsGenerated = (insights) => {
    setAiInsights(insights);
  };

  // Load recent analyses
  useEffect(() => {
    loadRecentAnalyses();
  }, []);

  // Notify parent about processing state
  useEffect(() => {
    if (onProcessingStateChange) {
      onProcessingStateChange(isProcessing);
    }
  }, [isProcessing, onProcessingStateChange]);

  const loadRecentAnalyses = async () => {
    setLoadingHistory(true);
    try {
      const baseUrl = getFullApiUrl('FINANCIAL_ANALYSIS').replace('/analyze-pdf', '');
      const response = await fetch(`${baseUrl}/history?limit=5`, {
        headers: addAuthHeader({})
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRecentAnalyses(result.analyses || []);
        }
      }
    } catch (error) {
      console.error('Error loading recent analyses:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLoadAnalysis = async (analysisId) => {
    try {
      const baseUrl = getFullApiUrl('FINANCIAL_ANALYSIS').replace('/analyze-pdf', '');
      const response = await fetch(`${baseUrl}/history/${analysisId}`, {
        headers: addAuthHeader({})
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.analysis) {
          setExtractedData({
            pivotData: result.analysis.pivot_data,
            years: result.analysis.years_analyzed
          });
          setIsDataLoaded(true);
          setAnalysisId(result.analysis.id);  // Simpan ID untuk keperluan cache insights
          
          // Load cached AI insights jika sudah ada
          if (result.analysis.ai_insights) {
            setAiInsights(result.analysis.ai_insights);
          } else {
            setAiInsights(null);
          }
          
          // Scroll to results
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error('Error loading analysis:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditTitle = (e, analysis) => {
    e.stopPropagation();
    setEditingTitle(analysis.id);
    setEditTitleValue(analysis.title || `Analysis ${analysis.id}`);
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  const handleSaveTitle = async (analysisId) => {
    if (!editTitleValue.trim()) {
      alert('Title tidak boleh kosong');
      return;
    }

    setIsSavingTitle(true);
    try {
      const baseUrl = getFullApiUrl('FINANCIAL_ANALYSIS').replace('/analyze-pdf', '');
      const response = await fetch(`${baseUrl}/history/${analysisId}/title`, {
        method: 'PATCH',
        headers: {
          ...addAuthHeader({}),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: editTitleValue.trim() })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Update local state
          setRecentAnalyses(prev => prev.map(a => 
            a.id === analysisId 
              ? { ...a, title: result.analysis.title }
              : a
          ));
          setEditingTitle(null);
          setEditTitleValue('');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Gagal mengupdate title');
      }
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Terjadi kesalahan saat mengupdate title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleKeyPress = (e, analysisId) => {
    if (e.key === 'Enter') {
      handleSaveTitle(analysisId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <main className="p-6">
      <div className="space-y-8 pb-20">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Laporan Keuangan Analysis</h1>
          <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
            Analisis laporan keuangan perusahaan dengan AI untuk mengekstrak metrik finansial dari Neraca dan Laba Rugi secara otomatis
          </p>
        </div>

        {/* Features Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-3 transition-colors duration-300">
          <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-3 flex items-center transition-colors duration-300">
            <i className="fa-solid fa-star mr-2 text-yellow-500 dark:text-yellow-400 text-xs"></i>
            Key Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-file-invoice-dollar text-primary-600 dark:text-primary-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Financial Metrics Extraction</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Ekstraksi otomatis metrik keuangan dari Neraca dan Laba Rugi dengan AI
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-chart-line text-blue-600 dark:text-blue-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Multi-Year Analysis</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Analisis laporan keuangan multi-tahun untuk melihat tren dan perkembangan
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 dark:bg-green-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-brain text-green-600 dark:text-green-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">AI Insights</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Dapatkan insight dan rekomendasi dari AI berdasarkan analisis laporan keuangan
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 dark:bg-purple-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-calculator text-purple-600 dark:text-purple-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Financial Ratios</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Perhitungan rasio keuangan otomatis untuk evaluasi kesehatan finansial
              </p>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <FinancialAnalysisUploadSection 
          onExtractionComplete={handleExtractionComplete}
          onProcessingChange={setIsProcessing}
        />
        
        {/* Results Section */}
        {isDataLoaded && (
          <>
            <FinancialInsightsCard 
              analysisResults={analysisResults} 
              aiInsights={aiInsights}
              onInsightsGenerated={handleInsightsGenerated}
              isDataLoaded={isDataLoaded}
              analysisId={analysisId}
            />
            <FinancialAnalysisResultsSection 
              extractedData={extractedData}
              isDataLoaded={isDataLoaded}
              onAnalysisCalculated={handleAnalysisCalculated}
            />
          </>
        )}

        {/* Recently Analysis Section */}
        {!isDataLoaded && recentAnalyses.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center transition-colors duration-300">
                <i className="fa-solid fa-clock-rotate-left mr-2 text-primary-600 dark:text-primary-400"></i>
                Recently Analysis
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
                Analisis yang pernah dilakukan oleh anggota group Anda
              </p>
            </div>
            <div className="p-6">
              {loadingHistory ? (
                <div className="text-center py-8">
                  <i className="fa-solid fa-spinner fa-spin text-2xl text-gray-400 dark:text-gray-500"></i>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 transition-colors duration-300">Memuat history...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentAnalyses.map((analysis) => (
                    <div
                      key={analysis.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors cursor-pointer"
                      onClick={() => handleLoadAnalysis(analysis.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-2">
                          <i className="fa-solid fa-file-invoice-dollar text-primary-600 mr-2"></i>
                          {editingTitle === analysis.id ? (
                            <div className="flex items-center flex-1 min-w-0">
                              <input
                                type="text"
                                value={editTitleValue}
                                onChange={(e) => setEditTitleValue(e.target.value)}
                                onKeyDown={(e) => handleKeyPress(e, analysis.id)}
                                onBlur={(e) => {
                                  // Delay to allow button clicks
                                  setTimeout(() => {
                                    if (document.activeElement !== e.target) {
                                      handleCancelEdit();
                                    }
                                  }, 200);
                                }}
                                autoFocus
                                className="flex-1 px-2 py-1 text-sm font-medium text-gray-800 dark:text-white bg-white dark:bg-gray-700 border border-primary-500 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300"
                                disabled={isSavingTitle}
                                maxLength={255}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveTitle(analysis.id);
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={isSavingTitle}
                                className="ml-2 px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                title="Simpan"
                              >
                                <i className={`fa-solid ${isSavingTitle ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={isSavingTitle}
                                className="ml-1 px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white text-xs rounded transition-colors"
                                title="Batal"
                              >
                                <i className="fa-solid fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center flex-1 min-w-0">
                              <h3 className="font-medium text-gray-800 dark:text-white truncate transition-colors duration-300">
                                {analysis.title || `Analysis ${analysis.id}`}
                              </h3>
                              <button
                                onClick={(e) => handleEditTitle(e, analysis)}
                                className="ml-1.5 p-1 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors flex-shrink-0"
                                title="Edit title"
                              >
                                <i className="fa-solid fa-pen text-xs"></i>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
                          <span>
                            <i className="fa-solid fa-user mr-1"></i>
                            {analysis.creator_name}
                          </span>
                          <span>
                            <i className="fa-solid fa-calendar mr-1"></i>
                            {formatDate(analysis.created_at)}
                          </span>
                          <span>
                            <i className="fa-solid fa-chart-line mr-1"></i>
                            {analysis.years_analyzed?.join(', ') || 'N/A'}
                          </span>
                          <span>
                            <i className="fa-solid fa-list mr-1"></i>
                            {analysis.records_count} metrik
                          </span>
                        </div>
                        {analysis.files && analysis.files.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {analysis.files.map((file, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 transition-colors duration-300"
                              >
                                <i className="fa-solid fa-file-pdf mr-1"></i>
                                {file.original_filename}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadAnalysis(analysis.id);
                        }}
                        className="ml-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors flex items-center"
                      >
                        <i className="fa-solid fa-eye mr-2"></i>
                        Load
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help Section */}
        {!isDataLoaded && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transition-colors duration-300">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
              <i className="fa-solid fa-question-circle mr-2 text-primary-600 dark:text-primary-400"></i>
              How to Use
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">1. Upload Laporan Keuangan:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Upload file PDF yang berisi Neraca dan Laba Rugi</li>
                  <li>• Bisa upload beberapa file untuk analisis multi-tahun</li>
                  <li>• Format yang didukung: PDF saja</li>
                  <li>• Maksimal total ukuran: 100MB untuk semua file</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">2. Ekstraksi Data:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Sistem akan melakukan Optical Character Recognition (OCR)</li>
                  <li>• Teknologi AI akan mengekstrak metrik keuangan secara otomatis</li>
                  <li>• Data akan diorganisir per periode untuk analisis tren</li>
                  <li>• Proses ekstraksi biasanya memakan waktu beberapa menit</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">3. Review & Analyze:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Lihat metrik keuangan yang diekstrak dalam format tabel</li>
                  <li>• Gunakan fitur "Generate AI Insights" untuk mendapatkan analisis mendalam</li>
                  <li>• Periksa rasio keuangan yang dihitung otomatis</li>
                  <li>• Bandingkan data antar tahun untuk melihat tren</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">4. Tips & Best Practices:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Pastikan PDF berisi Neraca dan Laba Rugi yang lengkap</li>
                  <li>• File dengan format tabel yang jelas akan memberikan hasil lebih akurat</li>
                  <li>• Untuk analisis multi-tahun, upload file terpisah untuk setiap tahun</li>
                  <li>• Verifikasi hasil ekstraksi dengan dokumen asli sebelum mengambil keputusan</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default FinancialAnalysisContent;

